import { useRef, useState, useEffect } from "react"
import webIcon from "assets/svg/globe.svg"
import mobileIcon from "assets/svg/tablet.svg"
import lernternIcon from "assets/svg/Lectern.svg"
import expandedIcon from "assets/svg/maximize-one.svg"
import calenderIcon from "assets/images/calendar-icon.png"
import expandIcon from "assets/images/expand-icon.png"
import addIcon from "assets/svg/plus.svg"
import AddReasonModal from "../components/AddReasonModal"
import { ExpandDrawer } from "../components/ExpandDrawer"
import { formatSentenceCase } from "../utils"

interface TimesheetRowProps {
  key: number | null
  day: string
  shiftPlan: string
  date: string
  shiftPlanType: string
  shiftPeriod: string
  clockIn: string
  clockOut: string | null
  method: string
  status: string
  flags: string[]
  reason: string
  showAddReasonModal: boolean
  timesheetId: number | null
  setShowAddReasonModal: React.Dispatch<React.SetStateAction<boolean>>
  timesheetQueryKey: string
}

export const TimesheetRow = ({
  day,
  shiftPlan,
  date,
  shiftPlanType,
  showAddReasonModal,
  setShowAddReasonModal,
  timesheetQueryKey,
  shiftPeriod,
  clockIn,
  status,
  clockOut,
  method,
  flags,
  reason,
  timesheetId,
}: TimesheetRowProps) => {
  const [showExpandDrawer, setShowExpandDrawer] = useState(false)
  const textRef = useRef<HTMLParagraphElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  useEffect(() => {
    const el = textRef.current

    if (el && reason) {
      setIsOverflowing(el.scrollHeight > el.clientHeight)
    }
  }, [reason])

  return (
    <div
      className="grid grid-cols-[100px_150px_150px_80px_80px_100px_150px_100px_100px] gap-3 items-center py-4 border-b border-b-slate-200 ml-4 min-w-max pr-4"
      style={{ fontFamily: '"Circular Std", sans-serif' }}
    >
      <p className="truncate min-w-0 hover:cursor-pointer sticky left-0 bg-white z-10 text-sm py-4">
        {day}
      </p>

      <div>
        <p
          className="truncate min-w-0 text-sm font-semibold"
          style={{ margin: "0" }}
        >
          {formatSentenceCase(shiftPlan)}
        </p>
        <small className="flex items-center gap-1">
          {" "}
          <img
            src={calenderIcon}
            alt="calender-icon"
          />
          <span className=" text-xs">{formatSentenceCase(shiftPlanType)}</span>{" "}
          plan
        </small>
      </div>

      <p className="text-sm">{shiftPeriod}</p>
      <p className="text-sm">{clockIn}</p>
      <p className="text-sm">{clockOut}</p>
      <p className="shrink-0 min-w-0 text-xs">
        <img
          src={
            method.toLowerCase() === "mobile-app"
              ? mobileIcon
              : method.toLowerCase() === "web-app"
              ? webIcon
              : lernternIcon
          }
          width={15}
          height={15}
        />{" "}
        {formatSentenceCase(method)}{" "}
      </p>

      <div className="flex flex-wrap gap-1">
        {flags.slice(0, 2).map((item, index) => (
          <p
            key={index}
            className="border border-slate-400 rounded-full px-3 py-1 flex justify-center text-xs"
          >
            {item}
          </p>
        ))}

        {flags.length > 2 && (
          <p
            onClick={() => setShowExpandDrawer(true)}
            className="border border-slate-400 rounded-full px-3 py-1 flex items-center gap-1 cursor-pointer text-xs"
          >
            {`+${flags.length - 2} flags`}
            <img
              src={expandIcon}
              alt={expandIcon}
            />
          </p>
        )}
      </div>

      {/* Reason */}
      <div className="">
        <p
          ref={textRef}
          className="min-w-0 line-clamp-3 text-xs"
        >
          {reason || ""}
        </p>

        {/* Rule 1: No reason */}
        {!reason && (
          <span
            onClick={() => setShowAddReasonModal(true)}
            className="flex items-center gap-1 cursor-pointer text-sm"
          >
            <img
              src={addIcon}
              width={15}
              height={15}
            />
            add reason
          </span>
        )}

        {/* Rule 3: More than 3 lines */}
        {reason && isOverflowing && (
          <span
            onClick={() => {
              setShowExpandDrawer(true)
            }}
            className="flex items-center gap-2 bg-[#F1F5F9] px-2 rounded-full w-fit cursor-pointer mt-1 text-xs"
          >
            <img
              src={expandedIcon}
              width={10}
              height={8}
            />
            Expand
          </span>
        )}
        <AddReasonModal
          open={showAddReasonModal}
          timesheetQueryKey={timesheetQueryKey}
          onClose={() => setShowAddReasonModal(false)}
          timesheetId={timesheetId}
        />
        <ExpandDrawer
          open={showExpandDrawer}
          onClose={() => setShowExpandDrawer(false)}
          title={`Overtime submission for ${new Date(date).toLocaleDateString(
            "en-US",
            {
              month: "long",
              day: "numeric",
              year: "numeric",
            }
          )}`}
          rows={[
            { label: "Day", value: <p className="text-sm">{day}</p> },
            {
              label: "Shift plan",
              value: (
                <div>
                  <p
                    className="text-sm font-semibold"
                    style={{ margin: "0" }}
                  >
                    {formatSentenceCase(shiftPlan)}
                  </p>
                  <small className="flex items-center gap-1">
                    <img
                      src={calenderIcon}
                      alt="calender-icon"
                    />
                    <span className="text-xs">
                      {formatSentenceCase(shiftPlanType)}
                    </span>{" "}
                    plan
                  </small>
                </div>
              ),
            },
            {
              label: "Shift period",
              value: <p className="text-sm">{shiftPeriod}</p>,
            },
            { label: "Clock-in", value: <p className="text-sm">{clockIn}</p> },
            {
              label: "Clock-out",
              value: <p className="text-sm">{clockOut}</p>,
            },
            {
              label: "Method",
              value: <p className="text-sm">{formatSentenceCase(method)}</p>,
            },
            {
              label: "Flag",
              value: (
                <div className="flex flex-wrap gap-1">
                  {flags.map((item, index) => (
                    <p
                      key={index}
                      className="border border-[#CAD5E2] rounded-full mt-1 w-fit text-[11px] px-3 py-1 text-xs text-[#0F172B]"
                    >
                      {item}
                    </p>
                  ))}
                </div>
              ),
            },
            { label: "Reason", value: <p className="text-sm">{reason}</p> },
            { label: "Status", value: <p className="text-sm">{status}</p> },
          ]}
        />
      </div>
      <p
        className={
          status === "Approved"
            ? "text-[#0F172B] capitalize text-xs"
            : status === "Rejected"
            ? "text-[#E7000B] capitalize text-xs"
            : "text-[#DA7705] capitalize text-xs"
        }
      >
        {status}
      </p>
    </div>
  )
}
